import { Component, EventEmitter, Input, OnInit, Output, DestroyRef, inject} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Area } from '@app/models/area.model'
import { Initiator, Target, Trigger, UpdateRuleNotificationInput } from '@app/models/message.model'
import { ListConfig, RadioConfig } from '@app/models/shared.model'
import { CommonService } from '@app/shared/services/common.service'
import { DynamicFormControlValidatorService } from '@app/shared/services/dynamic-form-control-validator.service'
import { PassService } from '@app/shared/services/pass.service'
import { RuleBasedNotificationsService } from '@app/shared/services/rule-base-messages.service'
import { TranslateMessagesService } from '@app/shared/services/translate-messages.service'
import { TranslateService } from '@ngx-translate/core'
import { isArray, isString } from 'lodash-es'
import { ConfirmationService } from 'primeng/api'
import { take } from 'rxjs'
import { UsersService } from '@app/shared/pages/settings/services/users.service'

/** Event entry held in eventsListConfig.data */
interface EventOption {
  id: number | string
  name: string
  areas: Area[]
}

@Component({ selector: 'app-ruled-message-modal', templateUrl: './ruled-message-modal.component.html', styleUrls: ['./ruled-message-modal.component.scss'], standalone: false })
export class RuledMessageModalComponent implements OnInit {
  private destroyRef = inject(DestroyRef)
  /** Current client ID */
  @Input() clientId: number

  /** Employees category id */
  @Input() employeesCategoryId: number

  /** Authorized category id */
  @Input() authorizedCategoryId: number

  /** Trigger types list */
  @Input() triggers: [{ name: Trigger; isDisabled: boolean }]

  /** Target types list */
  @Input() targets: Target[]

  /** Initiator types list */
  @Input() initiators: Initiator[]

  /** The name of current module */
  @Input() activeModuleName: string

  /** On save button clicked output */
  @Output() onSave = new EventEmitter<any>()

  /** On cancel button clicked output */
  @Output() onClose = new EventEmitter<any>()

  /** Is form visible */
  visible: boolean = false

  /** Form group */
  form: FormGroup

  /** Triggers list config */
  triggersListConfig: ListConfig = new ListConfig({
    data: [],
    optionValue: 'name',
    template: 'translated',
    translatePrevLabel: 'MESSAGES.TRIGGERS.',
    multiSelectionEmptyLabel: 'MESSAGES.FORMS.FIELD_LABELS.TRIGGERS',
    multiSelectionSeparationValue: ',',
    scrollHeight: '200px',
    optionDisabled: 'isDisabled',
    templateKey: 'name',
    selectedItemTemplate: true,
  })

  /** Events list config */
  eventsListConfig: ListConfig = new ListConfig({
    data: [],
    optionValue: 'id',
    optionLabel: 'name',
    filter: true,
    multiSelectionEmptyLabel: 'EVENTS.FORM_LABELS.VENUE_NAME',
    multiSelectionSeparationValue: ',',
  })

  /** Areas list config */
  areasListConfig: ListConfig = new ListConfig({
    data: [],
    optionValue: 'id',
    filter: true,
    template: 'valueFromCallback',
    multiSelectionEmptyLabel: 'SYSTEM.ENTITY_NAMES.AREAS',
    multiSelectionSeparationValue: ',',
    showClear: false,
    selectedItemTemplate: true,
    multiSelectionDisplay: 'comma',
    scrollHeight: '200px',
  })

  /** Target list config */
  targetListConfig: ListConfig = new ListConfig({
    data: [],
    template: 'translated',
    translatePrevLabel: 'MESSAGES.TARGET.',
    multiSelectionEmptyLabel: 'MESSAGES.FORMS.FIELD_LABELS.TARGET',
    multiSelectionSeparationValue: ',',
  })

  /** Initiator list config */
  initiatorListConfig: ListConfig = new ListConfig({
    data: [],
    filter: true,
    template: 'translated',
    translatePrevLabel: 'MESSAGES.INITIATOR.',
    multiSelectionEmptyLabel: 'MESSAGES.FORMS.FIELD_LABELS.INITIATORS',
    multiSelectionSeparationValue: ',',
  })

  /** Extra receivers list config */
  extraReceiversListConfig: ListConfig = {
    data: [],
    optionValue: 'id',
    filter: true,
    template: 'doubleWithSpace',
    templateKey: 'first_name',
    multiSelectionEmptyLabel: 'MESSAGES.FORMS.FIELD_LABELS.RECIPIENTS',
    multiSelectionSeparationValue: ',',
    secondTemplateKey: 'last_name',
    lazy: true,
    selectionLimit: 10,
    filterBy: 'first_name,last_name',
    scrollHeight: '200px',
    selectedItemTemplate: true,
    showClear: true,
    optionLabel: 'category',
  }

  /** CPortal users list config */
  cportalUsersListConfig: ListConfig = new ListConfig({
    data: [],
    optionValue: 'id',
    optionLabel: 'full_name',
    filter: true,
    multiSelectionDisplay: 'chip',
    multiSelectionEmptyLabel: 'MESSAGES.FORMS.FIELD_LABELS.ALL_USERS',
    scrollHeight: '200px',
    showClear: true,
  })

  /** Platform type radio config */
  platformRadioConfig: RadioConfig = new RadioConfig(true, 'keyera', 'gaterix', 'MESSAGES.PLATFORM.keyera', 'MESSAGES.PLATFORM.gaterix')

  model: any = {}

  /** Using for lazy loading */
  extraReceiversLastValue: string = '0'

  /** Latest recipients request — a response from an older one (e.g. before the events changed) is dropped */
  extraReceiversRequestId = 0

  /** Default form state */
  defaultFormState: any = {}

  sysMessage: any

  id: string = 'RULE_BASED_MESSAGES'


  constructor(
    private fb: FormBuilder,
    private ruleBaseService: RuleBasedNotificationsService,
    private formValidatorService: DynamicFormControlValidatorService,
    private passService: PassService,
    private commonService: CommonService,
    private translate: TranslateService,
    private translateMessagesService: TranslateMessagesService,
    private confirmationService: ConfirmationService,
    private userService: UsersService,
  ) {
    this.formValidatorService.init(() => this.isNew)
    this.sysMessage = this.translateMessagesService.create(this.id)
  }

  ngOnInit(): void {
    this.formBuild()
    this.addOrRemoveControlByPlatformValue()
    this.fetchCPortalUsers()
  }


  get isNew() {
    return this.model && Object.keys(this.model).length === 0
  }

  get filteredTriggers() {
    return !this.isKeyera
      ? this.triggers.filter((v) => v.name != 'unauthentic' && v.name != 'keep_open' && v.name != 'open_once_app' && v.name != 'station_disconnect')
      : this.triggers
  }

  get filteredInitiators() {
    const selectedTriggers = this.form?.get('trigger')?.value || []
    const isCPortalTriggerSelected = selectedTriggers.some((trigger: string) => trigger === 'cportal_login_success' || trigger === 'cportal_login_outside_hours')

    // If CPortal trigger IS selected, ONLY show cportal_user
    if (isCPortalTriggerSelected) {
      return ['cportal_user']
    }

    // If CPortal trigger is NOT selected, exclude cportal_user from options
    let filtered = (this.initiators as string[]).filter((v) => v !== 'all' && (this.isKeyera || v !== 'invited'))
    filtered = filtered.filter((v) => v !== 'cportal_user')

    return filtered
  }

  get filteredTargets() {
    let targets: any = this.targets || []

    if (this.isKeepOpenTrigger) targets = targets.filter((v: any) => v?.startsWith('auth') || v?.startsWith('emp'))
    else if (this.isLoginTriggerSelected) targets = targets.filter((v: any) => v === 'self')
    else targets = targets.filter((v: any) => !v?.startsWith('auth') && !v?.startsWith('emp'))

    if (!this.isTheInvitedInitiatorSelected) targets = targets.filter((v: Target) => v != 'inviter')

    return targets
  }

  get isTheInvitedInitiatorSelected() {
    const value = this.form?.get('initiator')?.value
    return value?.includes('invited') ?? false
  }

  get isKeepOpenTrigger() {
    return this.form.get('trigger')?.value[0] == 'keep_open'
  }

  get isUnauthenticTrigger() {
    return this.form.get('trigger')?.value[0] == 'unauthentic'
  }

  get isLoginTriggerSelected() {
    const triggers = this.form?.get('trigger')?.value || []
    return triggers.includes('cportal_login_success') || triggers.includes('cportal_login_outside_hours')
  }

  get isKeyera() {
    return this.form?.get('platform')?.value == 'keyera'
  }

  get position() {
    return document.dir == 'rtl' ? 'left' : 'right'
  }

  /** Selected event ids as numbers — the control holds string ids, as an array or a comma string before it is normalized */
  get selectedEventIds(): number[] {
    const value = this.form?.get('event_id')?.value
    const ids = isArray(value) ? value : isString(value) ? value.split(',') : []
    return ids.map((id: string | number) => +id).filter((id: number) => id > 0)
  }

  formBuild() {
    this.form = this.fb.group(
      {
        event_id: [{ value: [], disabled: false }, [Validators.required]],
        areas: [{ value: [], disabled: false }, []],
        trigger: [{ value: [], disabled: false }, [Validators.required]],
        initiator: [{ value: [], disabled: false }, [Validators.required]],
        platform: [{ value: 'keyera', disabled: false }, [Validators.required]],
        title: [{ value: '', disabled: false }, [Validators.required]],
        body: [{ value: '', disabled: false }, [Validators.required]],
        target: [{ value: '', disabled: false }, [Validators.required]],
        target_ids: [{ value: '', disabled: false }, [Validators.required]],
        trigger_user_ids: [{ value: '', disabled: false }, []],
      },
      {
        validator: this.formValidatorService.atLeastOneInputRequiredValidator(['target', 'target_ids']),
      },
    )
    this.defaultFormState = this.form.value

    this.form
      .get('event_id')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => {
        if (isString(v)) this.form.get('event_id')!.setValue(v?.split(','), { emitEvent: false })
        const selectedEvents = (this.eventsListConfig.data as EventOption[]).filter((e) => v && v.includes(e.id))

        this.areasListConfig.data = selectedEvents.flatMap((v) => v.areas)
        this.form
          .get('areas')!
          .setValue(
            this.form.get('areas')?.value ? this.form.get('areas')?.value?.filter((ar: number) => (this.areasListConfig.data as Area[]).find((arList) => +arList.id == +ar)) : null,
          )

        // Recipients are scoped to the selected events — reload them for the new selection
        this.extraReceiversLastValue = '0'
        this.extraReceiversListConfig.data = []
        this.fetchExtraReceivers()
      })

    this.form
      .get('platform')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.addOrRemoveControlByPlatformValue()
        this.triggersListConfig.data = this.filteredTriggers
        this.initiatorListConfig.data = this.filteredInitiators
        this.targetListConfig.data = this.filteredTargets

        this.form.get('initiator')!.setValue(!this.isKeyera ? this.form.get('initiator')?.value?.filter((v: Initiator) => v != 'invited') : this.form.get('initiator')!.value)
        this.form
          .get('trigger')!
          .setValue(!this.isKeyera ? this.form.get('trigger')!.value?.filter((v: Trigger) => v != 'unauthentic' && v != 'keep_open') : this.form.get('trigger')!.value)
        this.fetchExtraReceivers()
      })

    // Handle login trigger changes
    this.form
      .get('trigger')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_triggers) => {
        if (this.isLoginTriggerSelected) {
          // For login triggers: event_id and areas are not required and disabled
          this.form.get('event_id')!.clearValidators()
          this.form.get('event_id')!.setValue(null)
          this.form.get('event_id')!.disable()
          this.form.get('areas')!.setValue([])
          this.form.get('areas')!.disable()
          // Target IS required for login triggers
          this.form.get('target')!.setValidators([Validators.required])
          this.form.get('target')!.enable()
          this.form.get('trigger_user_ids')!.enable()
          // Auto-set initiator to cportal_user
          this.form.get('initiator')!.setValue(['cportal_user'])
        } else {
          // For other triggers: event_id IS required
          if (!Array.isArray(this.form.get('event_id')!.value)) {
            this.form.get('event_id')!.setValue([], { emitEvent: false })
          }
          this.form.get('event_id')!.setValidators([Validators.required])
          this.form.get('event_id')!.enable()
          this.form.get('areas')!.enable()
          this.form.get('target')!.setValidators([Validators.required])
          this.form.get('target')!.enable()
          this.form.get('trigger_user_ids')!.setValue(null)
          this.form.get('trigger_user_ids')!.disable()
          // Clear cportal_user from initiator if it was set
          const currentInitiator = this.form.get('initiator')!.value || []
          if (currentInitiator.includes('cportal_user')) {
            this.form.get('initiator')!.setValue(currentInitiator.filter((v: string) => v !== 'cportal_user'))
          }
        }
        // Update initiator list to show/hide cportal_user option
        this.initiatorListConfig.data = this.filteredInitiators
        // Update target list for login triggers to show only 'self' option
        this.targetListConfig.data = this.filteredTargets
        this.form.get('event_id')!.updateValueAndValidity()
      })
  }

  /**
   * Set default value to form
   */
  setDefaultValueToForm() {
    if (!this.isNew) {
      for (const key in this.model) {
        if (this.form.contains(key)) {
          const model = this.model as any
          this.form.get(key)?.setValue(model[key], { emitEvent: false })
        }
      }

      if (this.model.event_id) {
        this.form.get('event_id')!.setValue(this.model.event_id.toString())
      }
      this.form.get('areas')!.setValue(this.model.areas?.map((v: string) => v.toString()))

      this.targetListConfig.data = this.filteredTargets

      if (this.isKeepOpenTrigger) this.form.get('initiator')!.disable()

      this.updateTriggersDisableOption()

      if (this.model.platform == 'keyera') this.form.get('target')!.setValue(this.model.target)
      else this.form.clearValidators()
      this.addOrRemoveControlByPlatformValue()
    }
  }

  open(model: object, events: any) {
    this.model = model
    this.eventsListConfig.data = events
    this.triggersListConfig.data = this.filteredTriggers
    this.initiatorListConfig.data = this.filteredInitiators
    this.targetListConfig.data = this.filteredTargets

    this.formBuild()
    this.addOrRemoveControlByPlatformValue()
    this.fetchExtraReceivers()
    this.formValidatorService.switchFormValidator()
    this.setDefaultValueToForm()
    this.visible = true
  }

  cancel() {
    this.extraReceiversLastValue = '0'
    this.extraReceiversListConfig.data = []
    this.areasListConfig.data = []
    this.eventsListConfig.data = []
    this.targetListConfig.data = []
    this.triggersListConfig.data = []
    this.initiatorListConfig.data = []
    this.form.reset(this.defaultFormState, { emitEvent: false })
    this.form.enable({ emitEvent: false })
    this.close()
  }

  close() {
    this.visible = false
    this.onClose.emit(true)
  }

  onHide() {
    if (this.visible) {
      this.cancel()
    }
  }

  /**
   * Fetch CPortal users for trigger_user_ids dropdown
   */
  fetchCPortalUsers() {
    this.userService
      .getUsers()
      .pipe(take(1))
      .subscribe((users: any[]) => {
        this.cportalUsersListConfig.data = users
      })
  }

  /**
   * Fetch the employees  & authorized
   */
  fetchExtraReceivers() {
    // form.get, not form.contains — contains() is false for a DISABLED control, and triggers like
    // station_disconnect / unauthentic disable target while the recipients list still applies
    const target = this.form.get('target')
    if (target && (!target.value?.includes('employees') || !target.value?.includes('authorized'))) {
      if (!this.extraReceiversListConfig.data?.length) this.extraReceiversLastValue = '0'

      const eventIds = this.selectedEventIds
      const requestId = ++this.extraReceiversRequestId
      const data: any = {
        category: [],
        with_parents: false,
        event_ids: eventIds.length ? eventIds : null,
        status: 'active',
      }

      if (!this.form.get('target')?.value?.includes('employees')) data.category.push(this.employeesCategoryId)

      if (!this.form.get('target')?.value?.includes('authorized')) data.category.push(this.authorizedCategoryId)

      const pageOptions: any = {
        lastValue: this.extraReceiversLastValue,
        take: 200,
      }

      this.passService
        .getFilteredPasses(data, pageOptions)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (requestId !== this.extraReceiversRequestId) return

          // Handle both array response (old format) and object response (new format with pagination)
          const passes = Array.isArray(response) ? response : response.data || []

          if (!passes?.length) return
          else {
            this.extraReceiversListConfig.data = (
              this.extraReceiversListConfig.data?.length
                ? this.extraReceiversListConfig.data.concat(passes.filter((pass: any) => !this.extraReceiversListConfig.data.some((existing: any) => existing.id === pass.id)))
                : passes
            )
              .map((pass: any) => ({
                ...pass,
                category: pass.category === 10 ? 'employees' : pass.category === 11 ? 'authorized' : pass.category,
              }))
              .sort((a: any, _b: any) => (a.category === 'employees' ? -1 : 1))
          }
          this.extraReceiversLastValue = passes[passes?.length - 1]?.id?.toString() ?? '0'
        })
    }
  }

  /**
   * When the extra receivers list has lazy loading event fetch new data
   * @param event Lazy loading event
   */
  extraReceiversLazyLoading(event: any) {
    if (this.extraReceiversListConfig.data?.length == event.last) {
      this.fetchExtraReceivers()
    }
  }

  /**
   * Add and remove form control by the platform value
   */
  addOrRemoveControlByPlatformValue() {
    if (this.isKeyera) {
      this.form.addControl('target', new FormControl([]))
      this.form.addControl('target_ids', new FormControl(''))
      this.extraReceiversLastValue = '0'
      this.form
        .get('target')!
        .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          if (value?.includes('authorized') && value?.includes('employees')) {
            this.extraReceiversLastValue = '0'
            this.extraReceiversListConfig.data = []
          } else {
            if (
              value?.includes('authorized') ||
              value?.includes('employees') ||
              (!value?.includes('authorized') && !value?.includes('employees') && this.targetListConfig.data?.length > 1 && this.targetListConfig.data?.length <= 2)
            ) {
              this.extraReceiversLastValue = '0'
              this.extraReceiversListConfig.data = []
              this.fetchExtraReceivers()
            }
          }
        })
    } else {
      if (this.form.contains('target')) this.form.removeControl('target')
      if (this.form.contains('target_ids')) this.form.removeControl('target_ids')
    }

    this.form
      .get('initiator')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isKeyera) {
          this.targetListConfig.data = this.filteredTargets
          if (this.form.get('target')?.value?.includes('inviter')) this.form.get('target')!.setValue(this.form.get('target')!.value.filter((v: Target) => v != 'inviter'))
        }
      })

    this.form
      .get('trigger')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.targetListConfig.data = this.filteredTargets
        this.updateTriggersDisableOption()
      })

    if (!this.isKeyera) this.form.clearValidators()
    else this.form.addValidators(this.formValidatorService.atLeastOneInputRequiredValidator(['target', 'target_ids']))
  }

  checkAndGetForUpdate(model: UpdateRuleNotificationInput) {
    const update = this.commonService.getObjectDiff(this.model, model)

    for (const key in update) {
      if (!update[key] || update[key] == '') update[key] = null
    }
    return update
  }

  /**
   * Update the trigger disable option on the trigger change
   */
  updateTriggersDisableOption() {
    const selectedTrigger = this.form.get('trigger')!.value

    const isSpecialTriggerSelected = selectedTrigger.some((trigger: string) => trigger !== 'keep_open' && trigger !== 'unauthentic')

    // Check if any CPortal login trigger is selected
    const isCPortalTriggerSelected = selectedTrigger.some((trigger: string) => trigger === 'cportal_login_success' || trigger === 'cportal_login_outside_hours')

    // Check if any non-CPortal trigger is selected
    const isNonCPortalTriggerSelected = selectedTrigger.some((trigger: string) => trigger !== 'cportal_login_success' && trigger !== 'cportal_login_outside_hours')

    this.triggers.forEach((trigger: { name: string; isDisabled: boolean }) => {
      // CPortal triggers can only be combined with other CPortal triggers
      if (isCPortalTriggerSelected && trigger.name !== 'cportal_login_success' && trigger.name !== 'cportal_login_outside_hours') {
        trigger.isDisabled = true
      } else if (isNonCPortalTriggerSelected && (trigger.name === 'cportal_login_success' || trigger.name === 'cportal_login_outside_hours')) {
        trigger.isDisabled = true
      } else if (selectedTrigger.includes('keep_open')) {
        trigger.isDisabled = trigger.name !== 'keep_open'
      } else if (selectedTrigger.includes('unauthentic')) {
        trigger.isDisabled = trigger.name !== 'unauthentic'
      } else if (selectedTrigger.includes('station_disconnect')) {
        trigger.isDisabled = trigger.name !== 'station_disconnect'
      } else {
        trigger.isDisabled = isSpecialTriggerSelected && (trigger.name === 'keep_open' || trigger.name === 'unauthentic' || trigger.name === 'station_disconnect')
      }
    })

    if (selectedTrigger.includes('keep_open')) {
      this.form.get('areas')!.setValue([])
      this.form.get('areas')!.disable()
      this.form.get('initiator')!.setValue([])
      this.form.get('initiator')!.disable()
    } else if (selectedTrigger.includes('unauthentic')) {
      this.form.get('target')!.disable()
      this.form.get('target')!.setValue([])
      this.form.get('areas')!.disable()
      this.form.get('areas')!.setValue([])
    } else if (selectedTrigger.includes('station_disconnect')) {
      this.form.get('target')!.disable()
      this.form.get('areas')!.setValue([])
      this.form.get('areas')!.disable()
      this.form.get('initiator')!.setValue([])
      this.form.get('initiator')!.disable()
    } else {
      this.form.get('initiator')!.enable()
      this.form.get('areas')!.enable()
      this.form.get('target')!.enable()
    }
  }

  prepareForSave(model: any) {
    // Handle login triggers (client-level rules) differently
    const isLoginTrigger = model.trigger?.includes('cportal_login_success') || model.trigger?.includes('cportal_login_outside_hours')

    if (isLoginTrigger) {
      // Client-level rule - create only one rule
      const clientLevelRule: any = {
        ...model,
        client_id: +this.clientId,
        event_id: null,
        areas: null,
        trigger_user_ids: model.trigger_user_ids?.length ? model.trigger_user_ids.map((id: any) => +id) : null,
      }

      if (!this.isNew) {
        clientLevelRule.id = +this.model.id
      }

      const cleanedRule = this.commonService.cleanEmptyProperties(clientLevelRule)
      return [cleanedRule]
    }

    // Original logic for event-based rules
    if (!this.isNew) model.id = this.model.id
    if (!isArray(this.model.event_id) && !this.isNew) this.model.event_id = [this.model.event_id]
    if (!isArray(this.model.id) && !this.isNew) this.model.id = [this.model.id]

    let event_ids = this.isNew ? model.event_id : this.model.event_id

    //Get the current selected events
    const duplicatedObjects: any = Array.from({ length: event_ids.length }, () => ({
      ...model,
    }))

    let mergedArray: any = []

    // Check if there are object has removed (in the edit state)
    const objectsToRemove = !this.isNew && isArray(this.model.event_id) ? this.model.event_id.filter((v: number) => !model.event_id.map((j: string) => +j).includes(v)) : []

    if (objectsToRemove?.length && !this.isNew) {
      mergedArray = Array.from({ length: objectsToRemove.length }, () => ({
        ...this.model,
      }))
      mergedArray.forEach((element: any, index: number) => {
        element.event_id = objectsToRemove[index]
        element.active = false
      })
    }

    // Check if there are object has added (in the edit state)
    const objectsToAdd =
      !this.isNew && isArray(this.model.event_id)
        ? model.event_id.map((j: string) => +j).filter((v: number) => !this.model.event_id.includes(+v))
        : !this.isNew && !isArray(this.model.event_id)
          ? model.event_id.map((j: string) => +j).filter((v: number) => this.model.event_id != +v)
          : []

    if (objectsToAdd?.length && !this.isNew) {
      mergedArray = Array.from({ length: objectsToAdd.length }, () => ({
        ...this.model,
      }))
      mergedArray.forEach((element: any, index: number) => {
        element.event_id = objectsToAdd[index]
      })
    }

    if (!this.isNew) {
      let length = 1
      if (isArray(this.model.event_id)) {
        if (objectsToRemove?.length) length = duplicatedObjects.length - mergedArray.length
        else length = duplicatedObjects[0].event_id?.length - mergedArray?.length
      }
      mergedArray.push(
        ...Array.from(
          {
            length: length,
          },
          () => ({
            ...model,
          }),
        ),
      )
    } else
      mergedArray.push(
        ...Array.from(
          {
            length: duplicatedObjects.length,
          },
          () => ({
            ...model,
          }),
        ),
      )

    if (!this.isNew && isArray(this.model.event_id)) event_ids = event_ids.filter((v: any) => model.event_id.map((j: string) => +j).includes(v))

    mergedArray.forEach((element: any, index: number) => {
      if (!Object.hasOwn(element, 'active')) {
        element.event_id = !this.isNew && !isArray(this.model.event_id) ? +event_ids : +event_ids[this.isNew ? index : 0]
        if (isArray(this.model.event_id)) event_ids.shift()
      }
      element.client_id = +this.clientId

      if (element.areas) {
        const selectedAreas = (this.areasListConfig.data as Area[]).filter((ar) => +element.event_id == +ar.event_id && model.areas.map((b: string) => +b).includes(+ar.id))

        if (selectedAreas?.length) {
          element.areas = selectedAreas.map((v: Area) => +v.id)
        } else element.areas = []
      }

      if (this.isNew) element = this.commonService.cleanEmptyProperties(element)
      else {
        if (isArray(this.model.id) ? +this.model.id[0] : +element.id) {
          element.id = isArray(this.model.id) ? +this.model.id[0] : +element.id
          if (isArray(this.model.id)) this.model.id.shift()
        } else delete element.id
      }
    })
    return mergedArray
  }

  areasCallbackValue = (v: any) => {
    return v ? `${v.name} (${(this.eventsListConfig.data as EventOption[]).find((val) => +val.id == v.event_id)?.name})` : ''
  }

  /**
   * Save form data
   * @param e
   */
  save(e: any) {
    e?.preventDefault()
    if (this.form.valid) {
      const model = this.prepareForSave(this.form.value)
      if (this.isNew) {
        this.ruleBaseService
          .createNew(model)
          .pipe(take(1))
          .subscribe((data) => {
            if (data.success) {
              this.cancel()
              this.onSave.emit()
            }
          })
      } else {
        //    if (Object.keys(model)?.length > 1) {
        this.ruleBaseService
          .update(model)
          .pipe(take(1))
          .subscribe((data) => {
            if (data.success) {
              this.cancel()
              this.onSave.emit()
            }
          })
      }
      // }
    }
  }

  /**
   * Delete the rule based message
   * @param e
   */
  delete() {
    this.confirmationService.confirm({
      header: this.translate.instant('SYSTEM.MESSAGES.CONFIRM_DELETE_RULE.TITLE'),
      message: this.translate.instant('SYSTEM.MESSAGES.CONFIRM_DELETE_RULE.MESSAGE'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('COMMON.BUTTONS.DELETE'),
      rejectLabel: this.translate.instant('COMMON.BUTTONS.CANCEL'),

      accept: () => {
        this.ruleBaseService
          .delete(+this.model.id)
          .pipe(take(1))
          .subscribe((data) => {
            if (data.success) {
              this.cancel()
              this.onSave.emit()
            }
          })
      },
    })
  }
}
